import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

const ClientTable = ({
  clients,
  onBookAppointment,
  onEditClient,
  onViewProfile,
}) => {
  const { t } = useTranslation();
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON",
    }).format(amount || 0);

  const statusLabel = (status) =>
    status === "active"
      ? t("clients.statusActive")
      : status === "inactive"
      ? t("clients.statusInactive")
      : status === "vip"
      ? t("clients.statusVip")
      : status || "-";

  if (!clients?.length) {
    return null;
  }

  return (
    <div className="bg-background border border-border rounded-lg overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {t("clients.columnClient")}
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {t("clients.columnContact")}
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {t("clients.columnLastVisit")}
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {t("clients.appointmentsCount")}
            </th>
            <th className="px-4 py-3 text-left font-medium text-muted-foreground">
              {t("clients.totalSpent")}
            </th>
            <th className="px-4 py-3 text-right font-medium text-muted-foreground">
              {t("clients.columnActions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr
              key={client.id}
              className="border-t border-border hover:bg-muted/60"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Image
                    src={
                      client?.avatar ||
                      `https://api.dicebear.com/7.x/initials/svg?seed=${client?.name}`
                    }
                    alt={client?.name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div>
                    <button
                      type="button"
                      onClick={() => onViewProfile?.(client)}
                      className="text-sm font-medium hover:underline text-left"
                    >
                      {client?.name}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {statusLabel(client?.status)}
                    </div>
                  </div>
                </div>
              </td>

              <td className="px-4 py-3">
                <div className="space-y-0.5">
                  {client?.phone && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="Phone" size={12} />
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client?.email && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name="Mail" size={12} />
                      <span>{client.email}</span>
                    </div>
                  )}
                </div>
              </td>

              <td className="px-4 py-3 text-sm text-muted-foreground">
                {client?.lastVisit ? formatDate(client.lastVisit) : t("clients.never")}
              </td>

              <td className="px-4 py-3 text-sm">
                {client?.totalAppointments ?? 0}
              </td>

              <td className="px-4 py-3 text-sm">
                {formatCurrency(client?.totalSpent)}
              </td>

              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => onBookAppointment?.(client)}
                  >
                    {t("clients.schedule")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewProfile?.(client)}
                    aria-label={t("clients.viewProfile")}
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEditClient?.(client)}
                    aria-label={t("common.edit")}
                  >
                    <Icon name="Edit" size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTable;
